import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Space, Tag, message, Modal, Input, Select } from 'antd';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { EditOutlined, DeleteOutlined, LogoutOutlined, PlusOutlined } from '@ant-design/icons';
import { formatUTCToBeijing } from '../utils/time';

const { confirm } = Modal;
const { Option } = Select;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newRole, setNewRole] = useState('USER');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.listUsers();
      setUsers(response.data);
    } catch (error) {
      message.error('获取用户列表失败');
    }
  };

  const handleToggleStatus = async (userId, currentEnabled) => {
    confirm({
      title: '确认操作',
      content: `确定要${currentEnabled ? '禁用' : '启用'}该用户吗？`,
      onOk: async () => {
        try {
          await adminAPI.updateUser(userId, { enabled: !currentEnabled });
          message.success('操作成功');
          fetchUsers();
        } catch (error) {
          message.error('操作失败');
        }
      },
    });
  };

  const handleDelete = async (userId) => {
    confirm({
      title: '确认删除',
      content: '确定要删除该用户吗？此操作不可恢复！',
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await adminAPI.deleteUser(userId);
          message.success('删除成功');
          fetchUsers();
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const handleRevokeSessions = async (userId) => {
    confirm({
      title: '确认注销',
      content: '确定要注销该用户的所有会话吗？',
      onOk: async () => {
        try {
          await adminAPI.revokeUserSessions(userId);
          message.success('注销成功');
        } catch (error) {
          message.error('注销失败');
        }
      },
    });
  };

  const handleCreateUser = async () => {
    if (!newUsername) {
      message.error('请输入用户名');
      return;
    }

    try {
      const response = await adminAPI.createUser(newUsername, newRole);
      message.success(`用户创建成功，默认密码: ${response.data.default_password}`);
      setCreateModalVisible(false);
      setNewUsername('');
      setNewRole('USER');
      fetchUsers();
    } catch (error) {
      message.error('创建用户失败');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '角色',
      dataIndex: 'role_name',
      key: 'role_name',
      render: (role) => role || '无角色',
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled) => (
        <Tag color={enabled ? 'green' : 'red'}>
          {enabled ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? formatUTCToBeijing(text) : '-',
    },
    {
      title: '最后登录',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (text) => text ? formatUTCToBeijing(text) : '从未登录',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleToggleStatus(record.id, record.enabled)}
          >
            {record.enabled ? '禁用' : '启用'}
          </Button>
          <Button
            type="link"
            icon={<LogoutOutlined />}
            size="small"
            onClick={() => handleRevokeSessions(record.id)}
          >
            注销
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(record.id)}
          >
            删除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="用户管理"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateModalVisible(true)}
        >
          创建用户
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />
      <Modal
        title="创建用户"
        open={createModalVisible}
        onOk={handleCreateUser}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewUsername('');
          setNewRole('USER');
        }}
        okText="创建"
        cancelText="取消"
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>用户名:</div>
          <Input
            value={newUsername}
            onChange={(e) => setNewUsername(e.target.value)}
            placeholder="请输入用户名"
          />
        </div>
        <div>
          <div style={{ marginBottom: 8 }}>角色:</div>
          <Select
            value={newRole}
            onChange={setNewRole}
            style={{ width: '100%' }}
          >
            <Option value="USER">普通用户</Option>
            <Option value="ADMIN">管理员</Option>
          </Select>
        </div>
      </Modal>
    </Card>
  );
};

export default Users;
